import React, {useId, useRef} from 'react';

import Banner from '@/components/Banner/Banner';
import {AppButton} from '@/components/Button';
import CloseButton from '@/components/CloseButton/CloseButton';
import {StickyPageLayout} from '@/components/Layout';
import PopupResponsive from '@/components/Popup/PopupResponsive';
import PromotedBadge from '@/components/PromotedBadge/PromotedBadge';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import helpers from '@/utils/helpers';
import {twx} from '@/utils/tailwindUtils';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import AutoDeliveryBenefits from './AutoDeliveryBenefits';
import HowItWorksSection from './HowItWorksSection';

const AutoDeliveryDetailsStatusNoneTemplate = ({
    banner,
    headerKey,
    benefitsList,
    howItWorksList,
    amplitudeEvent,
    benefitsItemName,
    howItWorksItemName,
    onClose,
}) => {
    const isMobile = useIsMobileLayout();
    const popupRef = useRef(null);
    const headerId = useId();

    const {
        imageMedia,
        videoPath,
        isGradient,
        titleKey,
        subTitleKey,
        descriptionKey,
        buttonActionKey,
        action,
        actionAriaLabelKey,
    } = banner || {};
    const localizedStrings = getLocalizedStrings();
    const {redirectToExternalLink} = useConfigExternalLink(action);
    const isShowButton = buttonActionKey && localizedStrings[buttonActionKey] && redirectToExternalLink;

    useDocumentTitle(localizedStrings[headerKey]);

    const onButtonClick = () => {
        helpers.runFunction(amplitudeEvent);
        redirectToExternalLink();
    };
    const ButtonComponent = ({className, ...rest}) => {
        return (
            isShowButton && (
                <AppButton
                    primary
                    light
                    className={twx(
                        'mt-0 whitespace-nowrap md:min-w-[345px] [&>*]:overflow-hidden [&>*]:text-ellipsis',
                        className
                    )}
                    onClick={onButtonClick}
                    aria-label={getAriaLabel(actionAriaLabelKey)}
                    {...rest}
                >
                    {localizedStrings[buttonActionKey]}
                </AppButton>
            )
        );
    };

    return (
        <PopupResponsive
            contentRef={popupRef}
            className='overflow-auto text-start ml:ml-[calc(var(--sidebar-width)/2)] ml:max-h-[calc(100%-92px-var(--hw-h))] ml:w-[calc(100%-var(--sidebar-width)*1.5)] ml:max-w-[1016px] ml:rounded-xl ml:p-0'
            closeButtonClass='ml:text-romance'
            onClose={onClose}
            aria-labelledby={headerId}
        >
            {isMobile && (
                <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} containerRef={popupRef} />
            )}
            <StickyPageLayout
                className='items-center'
                headerOptions={{
                    endIcon: (
                        <CloseButton
                            onClick={onClose}
                            className='text-romance group-data-[is-sticked=true]:text-primary-dark'
                        />
                    ),
                    title: localizedStrings[headerKey],
                }}
                contentClassName='flex flex-1 flex-col items-center'
                footer={isMobile && <ButtonComponent />}
            >
                <Banner
                    className='h-[422px] bg-primary-dark ml:h-[308px]'
                    imageMedia={imageMedia}
                    videoPath={videoPath}
                    isGradient={isGradient}
                    gradient='linear-gradient(180deg, rgba(52, 48, 61, 0.00) 39.99%, rgba(28, 26, 33, 0.90) 100%)'
                >
                    <div className='absolute bottom-8 w-full px-4 text-start text-white ml:px-12'>
                        <div className='mb-2 flex ml:mb-4'>
                            <PromotedBadge
                                className='max-w-full whitespace-nowrap [&>*]:overflow-hidden [&>*]:text-ellipsis'
                                badgeText={localizedStrings[subTitleKey]}
                            />
                        </div>
                        <h1
                            id={headerId}
                            className='mb-2 overflow-hidden text-ellipsis whitespace-nowrap text-28 leading-[40px] ml:mb-4'
                        >
                            {localizedStrings[titleKey]}
                        </h1>
                        <p
                            className='line-clamp-3 whitespace-pre-line ml:max-w-96 ml:tracking-04'
                            dangerouslySetInnerHTML={{__html: localizedStrings[descriptionKey]}}
                        />
                        {!isMobile && <ButtonComponent dark className='mt-4' />}
                    </div>
                </Banner>
                <div className='ml:grid ml:grid-cols-2 ml:gap-8 ml:p-8'>
                    <AutoDeliveryBenefits
                        benefitsList={benefitsList}
                        itemName={benefitsItemName}
                        className='hidden p-4 py-8 has-[h2,li_*]:flex ml:m-0 ml:p-0'
                    />
                    <HowItWorksSection
                        howItWorksList={howItWorksList}
                        itemName={howItWorksItemName}
                        className='hidden border-t border-primary-dark-5 first:border-0 has-[h2,li_*]:flex ml:m-0 ml:border-0 ml:p-0'
                    />
                </div>
                {isMobile && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />}
            </StickyPageLayout>
        </PopupResponsive>
    );
};

export default AutoDeliveryDetailsStatusNoneTemplate;
